import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactGA from 'react-ga4';
import Page from '../components/Page';
import blogPosts, { RotationMockupGraphic, WebsiteFirstGraphic } from '../data/blogPosts';

const GRAPHIC_COMPONENTS = {
  RotationMockupGraphic,
  WebsiteFirstGraphic,
};

function renderSection(section, idx) {
  switch (section.type) {
    case 'p':
      return (
        <p key={idx} className="text-base text-slate-600 leading-relaxed mb-5">
          {section.content}
        </p>
      );

    case 'h2':
      return (
        <h2 key={idx} className="text-2xl font-black text-slate-900 mt-10 mb-4">
          {section.content}
        </h2>
      );

    case 'h3':
      return (
        <h3 key={idx} className="text-lg font-bold text-slate-900 mt-8 mb-3">
          {section.content}
        </h3>
      );

    case 'ul':
      return (
        <ul key={idx} className="text-base text-slate-600 leading-relaxed mb-5 ml-5 space-y-2">
          {section.items.map((item, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-indigo-500 shrink-0 mt-0.5">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'ol':
      return (
        <ol key={idx} className="text-base text-slate-600 leading-relaxed mb-5 ml-5 space-y-3">
          {section.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-indigo-600 font-black shrink-0 w-5">{i + 1}.</span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );

    case 'callout':
      return (
        <blockquote
          key={idx}
          className="border-l-4 border-indigo-500 bg-indigo-50 rounded-r-xl p-5 my-8"
        >
          <p className="text-base text-indigo-900 leading-relaxed italic">{section.content}</p>
        </blockquote>
      );

    case 'graphic': {
      const GraphicComponent = GRAPHIC_COMPONENTS[section.component];
      if (!GraphicComponent) return null;
      return (
        <div key={idx} className="rounded-2xl overflow-hidden my-8">
          <GraphicComponent />
        </div>
      );
    }

    case 'cta':
      return (
        <div
          key={idx}
          className="rounded-2xl bg-indigo-50 border border-indigo-100 p-8 text-center my-10"
        >
          <h3 className="text-xl font-black text-slate-900 mb-3">{section.heading}</h3>
          <p className="text-slate-600 text-sm leading-relaxed mb-6 max-w-md mx-auto">
            {section.content}
          </p>
          <Link
            to={section.buttonHref}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-6 py-3 rounded-xl no-underline transition-colors"
          >
            {section.buttonText}
          </Link>
        </div>
      );

    default:
      return null;
  }
}

function RelatedPostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group block rounded-2xl border border-slate-200 overflow-hidden no-underline hover:shadow-md hover:border-indigo-200 transition-all"
    >
      <div
        className="flex items-center justify-center py-8"
        style={{
          background: `linear-gradient(135deg, ${post.gradient.from}, ${post.gradient.to})`,
        }}
      >
        <span className="text-3xl">{post.icon}</span>
      </div>
      <div className="p-4">
        <p className="text-sm font-bold text-slate-900 mb-1 group-hover:text-indigo-700 transition-colors leading-snug">
          {post.title}
        </p>
        <p className="text-xs text-slate-400">{post.readTime}</p>
      </div>
    </Link>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();

  const post = blogPosts.find((p) => p.slug === slug);
  const related = post ? blogPosts.filter((p) => p.slug !== slug).slice(0, 3) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    ReactGA.send('pageview');
  }, [slug]);

  if (!post) {
    return (
      <Page seo={{ title: 'Post Not Found | Scheduly Blog' }}>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <p className="text-5xl mb-6">📭</p>
          <h1 className="text-2xl font-black text-slate-900 mb-4">Post Not Found</h1>
          <p className="text-slate-500 mb-8">
            We couldn't find a post at that address. It may have moved or never existed.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-6 py-3 rounded-xl no-underline transition-colors"
          >
            ← Back to Blog
          </Link>
        </div>
      </Page>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    datePublished: post.date,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    description: post.seo.description,
    url: post.seo.canonical,
    publisher: {
      '@type': 'Organization',
      name: 'Scheduly',
      url: 'https://scheduly.org',
    },
  };

  return (
    <Page seo={post.seo}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="max-w-2xl mx-auto px-6 pt-8 pb-2">
        <nav className="text-sm text-slate-400 flex items-center gap-1.5">
          <Link to="/blog" className="text-indigo-600 hover:underline no-underline font-medium">
            Blog
          </Link>
          <span>→</span>
          <span className="text-slate-500 truncate max-w-xs">{post.title}</span>
        </nav>
      </div>

      {/* Post header */}
      <div className="max-w-2xl mx-auto px-6 pt-6 pb-8 text-center">
        <div className="flex flex-wrap gap-2 justify-center mb-5">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        <h1 className="text-4xl font-black text-slate-900 leading-tight mb-5">{post.title}</h1>

        <div className="flex items-center justify-center gap-3 text-sm text-slate-400">
          <span>{post.author}</span>
          <span>·</span>
          <span>{post.date}</span>
          <span>·</span>
          <span>{post.readTime}</span>
        </div>
      </div>

      <hr className="border-slate-100 max-w-2xl mx-auto" />

      {/* Hero graphic */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="rounded-2xl overflow-hidden">
          {(() => {
            const HeroGraphic = post.heroGraphic && GRAPHIC_COMPONENTS[post.heroGraphic];
            return HeroGraphic ? <HeroGraphic /> : <RotationMockupGraphic />;
          })()}
        </div>
      </div>

      {/* Article body */}
      <article className="max-w-2xl mx-auto px-6 pb-16">
        {post.sections.map((section, idx) => renderSection(section, idx))}
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <div className="border-t border-slate-100 py-14 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 text-center">
              More from the Blog
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <RelatedPostCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
